import { useEffect, useState } from 'react';

import useDC from '@eneco/dc/useDC';
import { useMachine as useMachineUpdate } from '@xstate/react';

import { useCachedOfferMachine } from './useOfferMachine';
import { sendOrderMachine } from '../machines/sendOrder/sendOrder.machine';

interface UseSummaryStepMachinesOptions {
  /** When true, treat GET_EXTENSION as a loading state (Oxxio / retention). */
  includeExtensionFetching?: boolean;
}

export const useSummaryStepMachines = ({ includeExtensionFetching = false }: UseSummaryStepMachinesOptions = {}) => {
  const { businessUnit, label } = useDC();
  const [orderState, sendToOrderMachine] = useMachineUpdate(sendOrderMachine, {
    input: { businessUnit, label },
  });
  const { offerState, sendToOfferMachine } = useCachedOfferMachine(businessUnit, label);

  const [isLoading, setIsLoading] = useState(true);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const [isNotAvailable, setIsNotAvailable] = useState(false);
  const [isNotAuthenticated, setIsNotAuthenticated] = useState(false);
  const [isOrderFailed, setIsOrderFailed] = useState(false);

  useEffect(() => {
    setIsOrderFailed(orderState.matches('ERROR'));

    if (orderState.matches('ERROR')) {
      setIsSuccessful(false);
    }
  }, [orderState]);

  useEffect(() => {
    if (offerState.matches('IDLE')) return;

    const isFetching =
      offerState.matches('FETCHING') || (includeExtensionFetching && offerState.matches('GET_EXTENSION'));

    setIsLoading(isFetching);
    setIsFailed(!isFetching && !offerState.matches('SUCCESS'));
    setIsSuccessful(offerState.matches('SUCCESS'));
    setIsNotFound(offerState.matches('ERROR_NOT_FOUND'));
    setIsNotAvailable(offerState.matches('ERROR_NOT_AVAILABLE'));
    setIsNotAuthenticated(offerState.matches('ERROR_NOT_AUTHENTICATED'));
  }, [offerState, includeExtensionFetching]);

  return {
    label,
    orderState,
    sendToOrderMachine,
    offerState,
    sendToOfferMachine,
    isLoading,
    isSuccessful,
    setIsSuccessful,
    isFailed,
    isNotFound,
    isNotAvailable,
    isNotAuthenticated,
    isOrderFailed,
  };
};
